/**
 * Creating a session, and Edit scope (TMMAPP-0142 item 3). The pickers are built from
 * `GET /v1/me/assistant/options`:
 * - **Scope:** bank accounts, virtual accounts and envelopes the person can scope now. A granted
 *   shared virtual account carries "Shared with you"; picking it makes it the session's only scope
 *   (a sharer's session holds exactly one), and the dialog says so. Picking anything else drops it.
 * - **Data categories:** the registry's switches with their defaults and descriptions;
 *   subscriber-private ones are marked. In a sharer's session a category the registry doesn't allow
 *   there is greyed out and off, with "Not available on a shared virtual account.".
 * - **Edit scope** PATCHes scope and categories; narrowing either (an entity out, a category off)
 *   confirms in place first: "Earlier answers stay in the transcript".
 * - `422 ASSISTANT_SCOPE_INVALID` shows its catalog copy at the field it concerns.
 * - **Private or shared** (TMMAPP-0142 U3 item 15), chosen on create; New conversation's menu
 *   picks which one it opens on (`initialVisibility`, TMMAPP-0153 item 3). A shared session reads exactly
 *   one virtual account the person owns or holds a share on with assistant access (a choice of one),
 *   and follows a sharer's categories: what a sharer's session can't turn on is greyed out and off
 *   (a key with `sharerTools`, `PLANNED`, stays on for the virtual account's plan only),
 *   and a member's own data (`memberPrivate`) is never a session switch — each member adds theirs in
 *   the session. Editing a shared session changes its categories only; its virtual account is fixed.
 * - **A private room** (TMMAPP-0148 U1 item 3, `visibility: BREAKOUT`): its virtual account is its
 *   shared session's and never changes; its categories follow the shared session's rules, and each
 *   may be turned **off**, never on beyond the shared session (`parentCategories`): a key off there
 *   is greyed out and off, "Off in {shared session}, so it can't be on here." A `422
 *   PARENT_CATEGORY_OFF` says so at the categories.
 */
import { useMemo, useRef, useState, type FormEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import {
  assistantApi,
  type AssistantCategoryOption,
  type AssistantOptions,
  type AssistantScopable,
  type AssistantScopeEntity,
  type AssistantSession,
  type AssistantVisibility,
} from '@/api/assistant';
import { ErrorCodes, isApiError } from '@/api/errors';
import { errorCopy } from '@/api/errorCopy';
import { ConfirmPanel } from '@/components/ConfirmDialog';
import { DialogSubtitle } from '@/components/DialogSubtitle';
import { GuardedDialog } from '@/components/GuardedDialog';
import { Pill } from '@/components/Pill';
import { SubmitButton } from '@/components/SubmitButton';
import { useDialogDirtyGuard } from '@/shared/useDialogDirtyGuard';
import { useDialogFullScreen } from '@/shell/useDialogFullScreen';
import { formatList } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { resolveTokens } from '@/theme/theme';
import { categoryDescription, categoryLabel } from './assistantCopy';
import { blockedInShared, sharedRules, sharerCanUse, sharerToolsOnly } from './scopeGrant';

const NAME_MAX_LENGTH = 80;
const TITLE_ID = 'assistant-scope-dialog-title';
const SUBTITLE_ID = 'assistant-scope-dialog-subtitle';
const CONFIRM_TITLE_ID = 'assistant-scope-dialog-confirm-title';
const CONFIRM_BODY_ID = 'assistant-scope-dialog-confirm-body';

const entityKey = (entity: AssistantScopeEntity) => `${entity.kind}:${entity.id}`;

type FieldError = { field: 'scope' | 'categories' | 'visibility'; message: string } | null;

/**
 * Where a 422 ASSISTANT_SCOPE_INVALID belongs, and its copy. A private room's (TMMAPI-0184 U1):
 * `BREAKOUT_ANCHORED` (its virtual account never changes), and `PARENT_CATEGORY_OFF` naming the
 * categories its shared session holds off (`room`).
 */
function scopeInvalid(
  error: unknown,
  t: TFunction,
  room?: { sharedName: string; label: (key: string) => string; locale: string },
): FieldError {
  if (!isApiError(error) || error.code !== ErrorCodes.ASSISTANT_SCOPE_INVALID) return null;
  const backend = error.details.backend;
  const bag = typeof backend === 'object' && backend !== null ? backend : {};
  if (bag.reason === 'SHARED_VIRTUAL_ACCOUNT_ALONE') {
    return { field: 'scope', message: t('assistant.error.sharedAlone') };
  }
  if (bag.reason === 'BREAKOUT_ANCHORED') {
    return { field: 'scope', message: t('assistant.breakout.anchoredError') };
  }
  if (bag.reason === 'PARENT_CATEGORY_OFF') {
    const keys = Array.isArray(bag.categories)
      ? bag.categories.filter((key): key is string => typeof key === 'string')
      : [];
    return {
      field: 'categories',
      message:
        room === undefined || keys.length === 0
          ? t('assistant.breakout.parentOffErrorGeneric')
          : t('assistant.breakout.parentOffError', {
              categories: formatList(keys.map(room.label), room.locale),
              sharedName: room.sharedName,
            }),
    };
  }
  if (Array.isArray(bag.categories) && bag.categories.length > 0) {
    return { field: 'categories', message: t('assistant.error.sharerCategories') };
  }
  return { field: 'scope', message: t('assistant.error.scopeInvalid') };
}

interface SessionScopeDialogProps {
  options: AssistantOptions;
  /** Edit scope on this session; absent creates one. */
  session?: AssistantSession | undefined;
  /**
   * A private room's shared session (its name and categories): a category off there can't be on in
   * the room. Required for a room; ignored otherwise.
   */
  parent?: { name: string; categories: Record<string, boolean> } | undefined;
  /** Create only: the kind New conversation's menu chose (TMMAPP-0153 item 3); Private by default. */
  initialVisibility?: 'PRIVATE' | 'SHARED' | undefined;
  onClose: () => void;
  onSaved: (session: AssistantSession) => void;
}

export function SessionScopeDialog({
  options,
  session,
  parent,
  initialVisibility = 'PRIVATE',
  onClose,
  onSaved,
}: SessionScopeDialogProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const fullScreen = useDialogFullScreen();
  const editing = session !== undefined;
  const nameRef = useRef<HTMLInputElement>(null);

  const scopable = options.scopable;
  const initialSelection = useMemo(() => {
    if (session === undefined) return [] as string[];
    const reachable = new Set(scopable.map(entityKey));
    return session.scope.map(entityKey).filter((key) => reachable.has(key));
  }, [session, scopable]);
  // A private room reads its shared session's virtual account under the shared rules (item 3).
  const room = session?.visibility === 'BREAKOUT';
  const parentCategories = parent?.categories;
  const initialCategories = useMemo(() => {
    const out: Record<string, boolean> = {};
    for (const option of options.categories) {
      out[option.key] =
        (session?.categories[option.key] ?? option.defaultEnabled) &&
        // In a private room, what its shared session holds off reads off.
        (!room || parentCategories?.[option.key] === true);
    }
    return out;
  }, [session, options.categories, room, parentCategories]);

  const [name, setName] = useState(session?.name ?? '');
  const [selection, setSelection] = useState<string[]>(initialSelection);
  const [categories, setCategories] = useState<Record<string, boolean>>(initialCategories);
  const [nameError, setNameError] = useState(false);
  const [fieldError, setFieldError] = useState<FieldError>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmingNarrow, setConfirmingNarrow] = useState(false);
  const startVisibility: AssistantVisibility = session?.visibility ?? initialVisibility;
  const [visibility, setVisibility] = useState<AssistantVisibility>(startVisibility);
  // A shared session's one virtual account (create only).
  const [sharedOn, setSharedOn] = useState<string | null>(null);
  const sharedMode = visibility === 'SHARED' || room;
  const sharedVirtualAccounts = scopable.filter((item) => item.kind === 'VIRTUAL_ACCOUNT');

  const byKey = useMemo(() => new Map(scopable.map((item) => [entityKey(item), item])), [scopable]);
  const sharedPick = selection
    .map((key) => byKey.get(key))
    .find((item): item is AssistantScopable => item?.shared === true);
  // A sharer's session, or a shared one: a sharer's categories (and no member's own data).
  const sharer =
    sharedMode || sharedPick !== undefined || (session !== undefined && sharedRules(session));

  /** Off in the private room's shared session: it can't be on in the room. */
  const offInParent = (option: AssistantCategoryOption) =>
    room && parent?.categories[option.key] !== true;
  const blocked = (option: AssistantCategoryOption) =>
    offInParent(option) ||
    (sharer && (sharedMode ? blockedInShared(option) : !sharerCanUse(option)));

  /** The categories as they'll be sent: a sharer's or shared session holds every blocked one off. */
  const effective = (option: AssistantCategoryOption) =>
    (categories[option.key] ?? false) && !blocked(option);

  const dirty =
    name !== (session?.name ?? '') ||
    selection.join('|') !== initialSelection.join('|') ||
    visibility !== startVisibility ||
    sharedOn !== null ||
    options.categories.some((option) => effective(option) !== initialCategories[option.key]);
  const guard = useDialogDirtyGuard(dirty, onClose, busy);

  function toggle(item: AssistantScopable, checked: boolean) {
    const key = entityKey(item);
    setFieldError(null);
    setSelection((current) => {
      if (!checked) return current.filter((value) => value !== key);
      // A shared virtual account is the session's only scope; anything else replaces it.
      if (item.shared) return [key];
      return [...current.filter((value) => byKey.get(value)?.shared !== true), key];
    });
  }

  const narrowed =
    editing &&
    (initialSelection.some((key) => !selection.includes(key)) ||
      options.categories.some((option) => initialCategories[option.key] && !effective(option)));

  async function save() {
    setBusy(true);
    setFormError(null);
    setFieldError(null);
    const picked = sharedMode && !editing ? [sharedOn ?? ''] : selection;
    const scope = picked.flatMap((key) => {
      const item = byKey.get(key);
      return item === undefined ? [] : [{ kind: item.kind, id: item.id }];
    });
    const sent: Record<string, boolean> = {};
    for (const option of options.categories) sent[option.key] = effective(option);
    // Edit scope sends only what changed (the server audits each scope or category change). A
    // shared session's virtual account never changes.
    const scopeChanged =
      !(editing && sharedMode) && selection.join('|') !== initialSelection.join('|');
    const categoriesChanged = options.categories.some(
      (option) => sent[option.key] !== initialCategories[option.key],
    );
    try {
      if (session !== undefined && !scopeChanged && !categoriesChanged) {
        onClose();
        return;
      }
      const saved =
        session === undefined
          ? await assistantApi.create({
              name: name.trim(),
              scope,
              categories: sent,
              ...(sharedMode ? { visibility: 'SHARED' as const } : {}),
            })
          : await assistantApi.patch(session.id, {
              ...(scopeChanged ? { scope } : {}),
              ...(categoriesChanged ? { categories: sent } : {}),
            });
      onSaved(saved);
      onClose();
    } catch (error) {
      setConfirmingNarrow(false);
      const placed = scopeInvalid(
        error,
        t,
        room
          ? {
              sharedName: parent?.name ?? '',
              label: (key) => categoryLabel(t, key, options.categories),
              locale,
            }
          : undefined,
      );
      // A room shows no scope field: its scope refusal reads at the top of the form.
      if (placed !== null && room && placed.field === 'scope') setFormError(placed.message);
      else if (placed !== null) setFieldError(placed);
      else setFormError(errorCopy(t, error, 'assistant.error.generic'));
      guard.focusErrors();
      setBusy(false);
    }
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    if (!editing && name.trim().length === 0) {
      setNameError(true);
      guard.focusErrors();
      return;
    }
    if (!editing && sharedMode && sharedOn === null) {
      setFieldError({ field: 'scope', message: t('assistant.dialog.sharedPickRequired') });
      guard.focusErrors();
      return;
    }
    if (narrowed) {
      setConfirmingNarrow(true);
      return;
    }
    void save();
  }

  if (confirmingNarrow) {
    return (
      <GuardedDialog
        guard={guard}
        confirm={{
          titleId: CONFIRM_TITLE_ID,
          consequenceId: CONFIRM_BODY_ID,
          onClose: busy ? undefined : () => setConfirmingNarrow(false),
        }}
        fullWidth
        maxWidth="sm"
        fullScreen={fullScreen}
      >
        <ConfirmPanel
          titleId={CONFIRM_TITLE_ID}
          consequenceId={CONFIRM_BODY_ID}
          title={t('assistant.dialog.narrowTitle')}
          consequence={t('assistant.dialog.narrowConsequence')}
          confirmLabel={t('assistant.dialog.narrowConfirm')}
          busy={busy}
          focusHeading
          onConfirm={() => void save()}
          onCancel={() => setConfirmingNarrow(false)}
        />
      </GuardedDialog>
    );
  }

  const groups: { kind: AssistantScopable['kind']; label: string }[] = [
    { kind: 'ACCOUNT', label: t('assistant.dialog.accounts') },
    { kind: 'VIRTUAL_ACCOUNT', label: t('assistant.dialog.virtualAccounts') },
    { kind: 'ENVELOPE', label: t('assistant.dialog.envelopes') },
  ];
  const virtualAccountName = (id: number | null) =>
    id === null
      ? null
      : (scopable.find((item) => item.kind === 'VIRTUAL_ACCOUNT' && item.id === id)?.name ?? null);
  const help = { fontSize: 12, lineHeight: '18px', color: tokens.text2 } as const;

  return (
    <GuardedDialog
      guard={guard}
      titleId={TITLE_ID}
      subtitle={<DialogSubtitle id={SUBTITLE_ID}>{t('assistant.dialog.subtitle')}</DialogSubtitle>}
      subtitleId={SUBTITLE_ID}
      fullWidth
      maxWidth="sm"
      fullScreen={fullScreen}
      slotProps={{ transition: { onEntered: () => nameRef.current?.focus() } }}
    >
      <Box
        component="form"
        onSubmit={handleSubmit}
        noValidate
        sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}
      >
        <DialogTitle id={TITLE_ID}>
          {t(editing ? 'assistant.dialog.editTitle' : 'assistant.dialog.createTitle')}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ pt: 0.5 }}>
            {formError !== null && <Alert severity="error">{formError}</Alert>}
            {!editing && (
              <TextField
                label={t('assistant.dialog.name')}
                value={name}
                inputRef={nameRef}
                onChange={(event) => {
                  setName(event.target.value);
                  setNameError(false);
                }}
                required
                fullWidth
                error={nameError}
                helperText={nameError ? t('assistant.dialog.nameRequired') : undefined}
                slotProps={{ htmlInput: { maxLength: NAME_MAX_LENGTH } }}
              />
            )}
            {!editing && (
              <FormControl component="fieldset">
                <FormLabel component="legend">{t('assistant.dialog.visibility')}</FormLabel>
                <RadioGroup
                  value={visibility}
                  onChange={(event) => {
                    setVisibility(event.target.value === 'SHARED' ? 'SHARED' : 'PRIVATE');
                    setFieldError(null);
                  }}
                >
                  {(['PRIVATE', 'SHARED'] as const).map((value) => (
                    <FormControlLabel
                      key={value}
                      value={value}
                      control={<Radio />}
                      label={
                        <Box component="span" sx={{ display: 'block', py: 0.5 }}>
                          <Box component="span" sx={{ display: 'block' }}>
                            {value === 'SHARED'
                              ? t('assistant.dialog.visibilityShared')
                              : t('assistant.dialog.visibilityPrivate')}
                          </Box>
                          <Box component="span" sx={{ ...help, display: 'block' }}>
                            {value === 'SHARED'
                              ? t('assistant.dialog.visibilitySharedHelp')
                              : t('assistant.dialog.visibilityPrivateHelp')}
                          </Box>
                        </Box>
                      }
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            )}
            {sharedMode && editing && (
              <Typography variant="body2" data-shared-on="">
                {t(room ? 'assistant.breakout.readsOn' : 'assistant.dialog.sharedOn', {
                  name:
                    session?.scope.find((item) => item.kind === 'VIRTUAL_ACCOUNT')?.name ??
                    t('assistant.scopeKind.VIRTUAL_ACCOUNT'),
                })}
              </Typography>
            )}
            {sharedMode && !editing && (
              <FormControl component="fieldset" error={fieldError?.field === 'scope'}>
                <FormLabel component="legend">{t('assistant.dialog.sharedScope')}</FormLabel>
                <Typography sx={{ ...help, mb: 1 }}>
                  {t('assistant.dialog.sharedScopeHelp')}
                </Typography>
                {sharedVirtualAccounts.length === 0 ? (
                  <Typography variant="body2">{t('assistant.dialog.nothingToShare')}</Typography>
                ) : (
                  <RadioGroup
                    value={sharedOn ?? ''}
                    onChange={(event) => {
                      setSharedOn(event.target.value);
                      setFieldError(null);
                    }}
                    data-shared-pick=""
                  >
                    {sharedVirtualAccounts.map((item) => (
                      <FormControlLabel
                        key={entityKey(item)}
                        value={entityKey(item)}
                        control={<Radio />}
                        label={
                          <Box
                            component="span"
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 1,
                              flexWrap: 'wrap',
                            }}
                          >
                            <span>{item.name}</span>
                            {item.shared && (
                              <Pill wash="grey">{t('assistant.dialog.sharedWithYou')}</Pill>
                            )}
                          </Box>
                        }
                      />
                    ))}
                  </RadioGroup>
                )}
                {fieldError?.field === 'scope' && (
                  <FormHelperText>{fieldError.message}</FormHelperText>
                )}
              </FormControl>
            )}
            {!sharedMode && (
              <FormControl component="fieldset" error={fieldError?.field === 'scope'}>
                <FormLabel component="legend">{t('assistant.dialog.scope')}</FormLabel>
                <Typography sx={{ ...help, mb: 1 }}>{t('assistant.dialog.scopeHelp')}</Typography>
                {scopable.length === 0 && (
                  <Typography variant="body2">{t('assistant.dialog.nothingScopable')}</Typography>
                )}
                {groups.map((group) => {
                  const items = scopable.filter((item) => item.kind === group.kind);
                  if (items.length === 0) return null;
                  return (
                    <Box key={group.kind} sx={{ mt: 1 }} data-scope-group={group.kind}>
                      <Typography component="p" sx={{ fontSize: 13, fontWeight: 500 }}>
                        {group.label}
                      </Typography>
                      <FormGroup>
                        {items.map((item) => {
                          const key = entityKey(item);
                          const parent =
                            item.kind === 'ENVELOPE'
                              ? virtualAccountName(item.virtualAccountId)
                              : null;
                          return (
                            <FormControlLabel
                              key={key}
                              control={
                                <Checkbox
                                  checked={selection.includes(key)}
                                  onChange={(event) => toggle(item, event.target.checked)}
                                />
                              }
                              label={
                                <Box
                                  component="span"
                                  sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    flexWrap: 'wrap',
                                  }}
                                >
                                  <span>{item.name}</span>
                                  {parent !== null && (
                                    <Box component="span" sx={help}>
                                      {parent}
                                    </Box>
                                  )}
                                  {item.shared && (
                                    <Pill wash="grey">{t('assistant.dialog.sharedWithYou')}</Pill>
                                  )}
                                </Box>
                              }
                            />
                          );
                        })}
                      </FormGroup>
                    </Box>
                  );
                })}
                {sharer && (
                  <Alert severity="info" sx={{ mt: 1 }} data-shared-alone="">
                    {t('assistant.dialog.sharedAlone')}
                  </Alert>
                )}
                {fieldError?.field === 'scope' && (
                  <FormHelperText>{fieldError.message}</FormHelperText>
                )}
              </FormControl>
            )}
            <FormControl component="fieldset" error={fieldError?.field === 'categories'}>
              <FormLabel component="legend">{t('assistant.dialog.categories')}</FormLabel>
              <Typography sx={{ ...help, mb: 1 }}>
                {t('assistant.dialog.categoriesHelp')}
              </Typography>
              <FormGroup>
                {options.categories.map((option) => {
                  const off = blocked(option);
                  // Under a sharer's rules a key allowed for its sharer tools reads only the plan.
                  const description =
                    sharer && !off && sharerToolsOnly(option)
                      ? t('assistant.dialog.planOnly')
                      : categoryDescription(t, option.key, options.categories);
                  const blockedCopy = !off
                    ? null
                    : room && !blockedInShared(option)
                      ? t('assistant.breakout.parentOff', { sharedName: parent?.name ?? '' })
                      : sharedMode && option.memberPrivate
                        ? t('assistant.dialog.memberOwn')
                        : sharedMode
                          ? t('assistant.dialog.notInShared')
                          : t('assistant.dialog.notOnShared');
                  return (
                    <Box key={option.key} data-category={option.key} sx={{ py: 0.5 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={effective(option)}
                            disabled={off}
                            onChange={(event) =>
                              setCategories((current) => ({
                                ...current,
                                [option.key]: event.target.checked,
                              }))
                            }
                          />
                        }
                        label={
                          <Box
                            component="span"
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 1,
                              flexWrap: 'wrap',
                            }}
                          >
                            <span>{categoryLabel(t, option.key, options.categories)}</span>
                            {option.subscriberPrivate && !sharedMode && (
                              <Pill wash="grey">{t('assistant.dialog.subscriberPrivate')}</Pill>
                            )}
                          </Box>
                        }
                      />
                      {(description !== null || blockedCopy !== null) && (
                        <Typography sx={{ ...help, paddingInlineStart: 6 }}>
                          {blockedCopy ?? description}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </FormGroup>
              {fieldError?.field === 'categories' && (
                <FormHelperText>{fieldError.message}</FormHelperText>
              )}
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={guard.requestClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <SubmitButton type="submit" loading={busy}>
            {t(editing ? 'assistant.dialog.save' : 'assistant.dialog.create')}
          </SubmitButton>
        </DialogActions>
      </Box>
    </GuardedDialog>
  );
}
